import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, Button, Card, Container, Alert, InputGroup } from 'react-bootstrap';
import { AppContext } from "../context/appContext";
import { Auth } from "aws-amplify";
import { Eye, EyeSlash } from "react-bootstrap-icons";

function ResetPasswordConfirm() {
  const { setGlobalLoading } = useContext(AppContext);
  const [username, setUsername] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Get username from URL query parameter
    const queryParams = new URLSearchParams(location.search);
    const usernameParam = queryParams.get('username');
    if (usernameParam) {
      setUsername(usernameParam);
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setGlobalLoading(true);
      // Complete the password reset process
      await Auth.forgotPasswordSubmit(username.toLowerCase(), code, newPassword);
      setSuccess('Your password has been reset successfully!');
      setTimeout(() => {
        navigate('/reset-password-success');
      }, 2000);
      setGlobalLoading(false);
    } catch (error) {
      console.error('Reset password error:', error);
      setError(error.message || 'An error occurred while resetting your password');
      setGlobalLoading(false);
    }
  };

  return (
    <Container
      className="d-flex align-items-center justify-content-center"
      style={{ minHeight: "100vh" }}>
      <Card
        className="shadow"
        style={{ maxWidth: "400px", width: "100%", borderRadius: "10px" }}>
        <Card.Body>
          <h2>Reset Your Password</h2>
          <div className="text-center mb-4">
            <img
              src="/faithwelcomeback.png"
              alt="Welcome"
              style={{ maxWidth: "100%", height: "auto", borderRadius: "8px" }}
            />
          </div>
          <Alert variant="info" className="mb-4">
            <p className="mb-0"><strong>Password Reset Instructions:</strong></p>
            <ol className="pl-3 mb-0 mt-2" style={{ paddingLeft: "20px" }}>
              <li>Check your email for a verification code we've sent you</li>
              <li>Enter the 6-digit verification code below</li>
              <li>Create and confirm your new password</li>
            </ol>
          </Alert>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="username">
              <Form.Label>Email address</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter your email address"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase())}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="code">
              <Form.Label>Verification Code</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter the 6-digit code from your email"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
              <Form.Text className="text-muted">
                Enter the 6-digit verification code that was sent to your email address. If you don't see it in your inbox, please check your spam folder.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="newPassword">
              <Form.Label>New Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
                <InputGroup.Text
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ cursor: "pointer", background: "white", borderLeft: "none" }}
                >
                  {showPassword ? <EyeSlash /> : <Eye />}
                </InputGroup.Text>
              </InputGroup>
              <Form.Text className="text-muted">
                Password must be at least 8 characters long.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="confirmPassword">
              <Form.Label>Confirm New Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <InputGroup.Text
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{ cursor: "pointer", background: "white", borderLeft: "none" }}
                >
                  {showConfirmPassword ? <EyeSlash /> : <Eye />}
                </InputGroup.Text>
              </InputGroup>
            </Form.Group>

            <div className="d-grid gap-2">
              <Button variant="primary" type="submit" className="rounded-pill btn-accent">
                Reset Password
              </Button>
              <div>
                Remember your password?{" "}
                <span
                  style={{
                    color: "#0d6efd",
                    cursor: "pointer"
                  }}
                  onClick={() => navigate("/login")}>
                  Back to login
                </span>
              </div>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default ResetPasswordConfirm; 